// SHARED PARCEL-YEAR SCOPE — the fetch and row assembly both parcel-grained reports run on.
//
// Property Executive Summary and Outstanding Appeals ask the same question of the database (every
// account-year for a client, gated on representation, with values, status, assessor and tax) and
// differ only in which rows they keep and how they summarise. Extracted so the two cannot drift:
// a fix to the tax derivation or the representation gate lands in both at once.
//
// ⛔ REUSABLE BEFORE PER-REPORT. A second report copy-pasting this fetch is how the old
// PropertyExecutiveSummary page came to have no representation gate while six RPCs had one.

import { fetchAll, fetchByIds, fetchByParcelIds, mapWithConcurrency } from './pagination';
import { gateParcels } from './represented';
import { resolveAssessorsForParcels } from './assessor';
import { splitJurisdictionLines, normaliseRate, resolveRateUnit, aggregateTax, aggregateSavings, pickYearBill, printedBillRate, effectiveBillRate, yearAdValorem, isSupplementalBill } from './taxRate';
import { toNumber, PENDING } from './format';
import { appealLevelLabel } from './appealLevels';
import { primaryAppealRow, ratePercentOf, columnRateRow, sameJurisdictionRow } from '../utils/appealRows';
import { jurisdictionLabel } from '../utils/jurisdictionLabel';
import { baselineSavings, baseRateSource } from '../utils/baselineSavings';
import { billSavings } from '../utils/billSavings';
import { resolveFinalLevel, levelOrdinal } from '../utils/finalLevel';
import { APPEAL_STATUS } from '../constants/statusCodes';
import { TAX_BILL_STATUS } from '../constants/statuses';
import { prorataShares } from '../utils/prorata';

// EffectiveTaxRate when a year has no rate of its own: Taxes ÷ Final, in percent units (the 'rate' column type).
// Keeps Taxes' estimate mark; null when either side is missing, pending, or Final is not above 0.
export function effectiveRate(taxes, final) {
  const t = toNumber(taxes), f = toNumber(final);
  if (t == null || f == null || f <= 0) return null;
  const v = (t / f) * 100;
  return taxes && typeof taxes === 'object' && taxes.estimated ? { value: v, estimated: true } : v;
}

const key = (p, y) => `${p}|${y}`;

// Pick the bill whose array we trust, and shout about any other that disagrees.
// The tax_bills columns parcelYearFigures reads — the reports' fetch and the parcel card's (ParcelModal) both use
// this, so the two cannot select different fields and disagree. billed_amount and the three totals are what tell a
// FILED $0 BILL (zeroBilledYear) apart from a year with no usable lines.
// collector_id and supplemental_seq let billedYearTotal match each scheduled installment to its collector's schedule.
// total_tax_rate, taxable_value and bill_type give the year's bill rate when its lines carry none (printed, else effective).
// total_bill_amount is a supplemental's amount when it has no billed_amount (supplementalYearTotal) — Palm Springs
// 009-605-411-0 2025 and 18 East 2022 were entered before billed_amount was filled, and added $0 without it.
export const PARCEL_YEAR_BILL_COLS =
  'parcel_id,tax_year,collector_id,installment_number,supplemental_seq,bill_type,status,superseded_at,jurisdiction_rates,billed_amount,total_bill_amount,total_ad_valorem,total_non_ad_valorem,penalty_fees,total_tax_rate,taxable_value,equalization_factor';

// ═══ A FILED $0 BILL REPORTS $0.00 (Philip, 2026-10-11) ═══
// Tampa Airport 1124088000 2021–2025: fully exempt (GO exemption), taxable $0, every year's bill filed with Billed
// $0.00 — and Taxes read BLANK, because Taxes come only from a bill's jurisdiction lines, and an exempt bill has none
// usable (no lines, or rated lines at $0 on a $0 base whose rate unit cannot be told, which the rate band refuses).
// With no lines this year the year could even borrow a PRIOR year's rate and print an estimate. A bill that says the
// tax is $0 IS the answer: Taxes $0.00, not blank, not estimated.
// True when the year has at least one current, un-rejected bill and EVERY such bill's full tax is 0 — billed_amount
// when stored (the full tax before discount; a PAID bill's amount due is $0 but its billed is not, which is why the
// amount due is never read here), else ad valorem + non-ad valorem + penalty — and no bill carries a line amount.
// Regular bills only: a supplemental is added on top of the year's Taxes (supplementalYearTotal), never decides them.
export function zeroBilledYear(bills) {
  const live = (bills || []).filter((b) => b && !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED && !isSupplementalBill(b));
  if (!live.length) return false;
  return live.every((b) => {
    const parts = [b.total_ad_valorem, b.total_non_ad_valorem, b.penalty_fees].map(toNumber);
    const full = toNumber(b.billed_amount) ?? (parts.some((p) => p != null) ? parts.reduce((s, p) => s + (p || 0), 0) : null);
    if (full == null || Math.abs(full) >= 0.005) return false;
    const lines = Array.isArray(b.jurisdiction_rates) ? b.jurisdiction_rates : [];
    return lines.every((l) => !toNumber(l?.amount));
  });
}

// ═══ THE BILLED TOTAL — a year whose bills carry no lines (Philip, 2026-10-11) ═══
// IL 12-08-100-006-8531 / 2021: both Cook installments entered by hand from a Treasurer payment summary ($7,274.45 +
// $4,665.50). A bill entered by hand has no jurisdiction lines, so Taxes read BLANK — though the year's tax is known
// to the cent. When the year has no lines of its own, Taxes = Σ the scheduled installments' billed_amount: the full tax
// of each installment, as the card's Billed column shows it. Never the cards' "Ad Valorem (annual)", which repeats the
// annual figure on every installment and would double it.
//
// ⛔ ONLY WHEN THE YEAR IS COMPLETE — a partial sum printed as the year's tax is worse than a blank. Every collector the
// parcel has that year (active parcel_collectors, plus any collector with a bill) must have a schedule
// (collector_installments for the year and the parcel's RE / PP kind), and exactly one current, un-rejected bill with a
// billed_amount for each scheduled installment. Supplementals (off-schedule) are not part of the scheduled annual and
// are not counted, as on the card. A Real-and-Personal parcel has no single kind to check against, so it gets no
// billed total. Anything short of that → null, and the year falls through to the prior-year estimate as before.
//   bills          this parcel's bills for the year           collectors  Set of collector ids linked that year
//   scheduleOf(c)  that collector's scheduled installment numbers for the year and kind (array; empty = none)
export function billedYearTotal({ bills, collectors = new Set(), scheduleOf }) {
  const sched = (bills || []).filter((b) => b && !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED
    && b.installment_number != null && b.supplemental_seq == null);
  if (!sched.length || typeof scheduleOf !== 'function') return null;
  const need = new Set([...collectors, ...sched.map((b) => b.collector_id)]);
  let total = 0;
  for (const c of need) {
    const insts = scheduleOf(c) || [];
    if (!insts.length) return null;
    const got = new Map();
    for (const b of sched.filter((x) => x.collector_id === c)) {
      const n = Number(b.installment_number);
      const amt = toNumber(b.billed_amount);
      if (got.has(n) || amt == null) return null;          // two current bills for one installment, or no billed figure
      got.set(n, amt);
    }
    if (got.size !== insts.length || !insts.every((n) => got.has(Number(n)))) return null;
    for (const v of got.values()) total += v;
  }
  return Math.round(total * 100) / 100;
}

// ═══ SUPPLEMENTALS ADD TO THE YEAR'S TAXES, NEVER SET ITS RATE (Philip, 2026-10-29) ═══
// A supplemental is a prorated mid-year bill on its own fiscal year's rate (Alameda's unsecured roll is billed at the
// prior year's rate, so the two differ). Σ the year's current, un-rejected supplementals' billed amounts, or null when
// the year has none.
export function supplementalYearTotal(bills) {
  const supp = (bills || []).filter((b) => b && !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED && isSupplementalBill(b));
  if (!supp.length) return null;
  return Math.round(supp.reduce((s, b) => s + (toNumber(b.billed_amount) ?? toNumber(b.total_bill_amount) ?? 0), 0) * 100) / 100;
}

// ⛔ A REJECTED BILL IS NOT THE YEAR'S BILL (Philip, 2026-10-11). Someone said it is wrong; it was read here anyway,
// so a rejected 2025 bill carrying lines made 2025 an ACTUAL year — its lines' total printed as Taxes with no estimate
// mark (Orlando 03-24-30-0000-10-164 / 2025 candidate; billSavings and zeroBilledYear already skipped rejected bills).
// ⛔ NOR IS A SUPPLEMENTAL (Philip, 2026-10-29). It has no installment number, so `?? 0` sorted it ahead of installment 1
// and its prorated lines became the year's Taxes and rate (prop-1376 2022; DTAG Oakland 2021–2024).
function pickRateLines(bills, label) {
  const live = (bills || [])
    .filter((b) => !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED && !isSupplementalBill(b)
      && Array.isArray(b.jurisdiction_rates) && b.jurisdiction_rates.length)
    .sort((a, b) => (a.installment_number ?? 0) - (b.installment_number ?? 0));
  if (!live.length) return null;
  const chosen = live[0];
  const chosenTotal = splitJurisdictionLines(chosen.jurisdiction_rates).total;
  live.slice(1).forEach((other) => {
    const t = splitJurisdictionLines(other.jurisdiction_rates).total;
    if (t != null && chosenTotal != null && Math.abs(t - chosenTotal) > 0.01) {
      console.warn(
        `[${label}]: instalment ${other.installment_number} carries a DIFFERENT ` +
        `jurisdiction_rates array (Σ ${t.toFixed(2)}) from instalment ${chosen.installment_number} ` +
        `(Σ ${chosenTotal.toFixed(2)}). Using the lowest instalment. The array is annual — this is a ` +
        'real discrepancy to investigate, not something the report reconciles.'
      );
    }
  });
  return chosen.jurisdiction_rates;
}

// ═══ THE BILL'S PRINTED TOTALS WIN OVER LINES THAT ADD UP TO MORE (Philip 2026-10-30) ═══
// Clark County NV 162-22-402-002 2024: the levy line is taxable × rate ($26,629.09) and the tax-cap abatement comes off
// only in the bill's printed totals (ad valorem $18,649.67 + non-ad valorem $2.60), so Σ lines overstated Taxes by
// ~$8K in every year. When the year's line bill prints an ad valorem total, its lines come to more than ad valorem +
// non-ad valorem by over $1 (rounding), AND that printed sum is the year's complete billed total (billedYearTotal: every
// scheduled installment in, within $1), the printed sum is the year's tax. → the printed sum, or null (lines stand).
// ⛔ THE BILLED-TOTAL MATCH IS THE GUARD: many bills print ONE INSTALLMENT's ad valorem beside annual lines (Cook, HI);
// without it their Taxes would halve. The rate is untouched: it stays the bill's.
const PRINTED_TOLERANCE = 1;
export function printedOverLines(bills, lines, linesTotal, billedTotal) {
  const bill = (bills || []).find((b) => b && b.jurisdiction_rates === lines);
  const adv = toNumber(bill?.total_ad_valorem);
  if (adv == null || linesTotal == null || billedTotal == null) return null;
  const printed = Math.round((adv + (toNumber(bill.total_non_ad_valorem) || 0)) * 100) / 100;
  return linesTotal - printed > PRINTED_TOLERANCE && Math.abs(printed - billedTotal) <= PRINTED_TOLERANCE ? printed : null;
}

// The step 8 report options (src/reports/parcelYearRows reads them). Defaults = the report as it always was.
export const NOTICE_OPTIONS = [
  // One row of chips: Original, each active Notice Type, All. Several at once; Original only = the report as before.
  // 'current' (the default): each year's value, a Corrected notice replacing the original (buildParcelYearRows).
  { key: 'notices', type: 'notices', label: 'Notices', default: ['current'] },
  { key: 'nonNtrg', type: 'toggle', label: 'Non-NTRG savings', options: [{ value: 'exclude', label: 'Leave out' }, { value: 'include', label: 'Include' }], default: 'exclude' },
  { key: 'projected', type: 'toggle', label: 'Pending savings', options: [{ value: 'exclude', label: 'Pending' }, { value: 'include', label: 'Projected' }], default: 'exclude' },
];

// The levels a report can lay side by side (optional "<Level> Value / Taxes / Savings" columns — step 8).
const LEVEL_KEYS = ['proposed', 'appeal_one', 'appeal_two', 'appeal_three', 'appeal_four'];
const LEVEL_WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
// The level key at an ordinal (utils/finalLevel levelOrdinal's inverse): 0 → proposed, 1 → appeal_one …
const levelKeyAt = (n) => (n <= 0 ? 'proposed' : `appeal_${LEVEL_WORDS[n - 1] || n}`);

export const PARCEL_YEAR_COLUMNS = [
  // ⛔ LOCKED: Year and Account # are the only columns that cannot be hidden — together they identify the row
  // (Philip, 2026-10-10). Every other column can be switched off, on screen and in every export.
  { key: 'tax_year', label: 'Year', type: 'year', locked: true },
  // THE EXPORT'S BASELINE MARKER. The screen shows a BASELINE pill on the row; a client opening the XLSX or CSV
  // sees only cells, and this is the cell the baseline footnote points at. It was un-hideable for that reason;
  // since 2026-10-10 it can be hidden like any other column (Philip). Hidden, the footnote has no marker to point
  // at, so the export carries BASELINE_UNMARKED_FOOTNOTE instead (viewModel): it still says baseline figures are
  // in the file and that no savings are claimed on them, and says the Note column is what marks them.
  // The Note column (was "Engagement"): 'Baseline year' or 'Non-NTRG'. Shown only when some row has one (showWhenUsed).
  { key: 'engagement', label: 'Note', type: 'text', showWhenUsed: true },
  { key: 'property', label: 'Property', type: 'text' },
  { key: 'account_number', label: 'Account #', type: 'text', locked: true },
  { key: 'assessor', label: 'Assessor', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'appeal_level', label: 'Level', type: 'text' },
  { key: 'appeal_status', label: 'Status', type: 'status' },
  { key: 'proposed_value', label: 'Proposed', type: 'value' },
  { key: 'final_value', label: 'Final', type: 'value' },
  // ⛔ REDUCTION AND RATE SIT BETWEEN THE VALUES AND THE TAX COLUMNS, matching her workbook. The
  // reading order is the argument: what it's worth, then what changed, then what that cost.
  // Appending them at the end put the consequence before the cause.
  //
  // optional = hidden on screen by default. exportDefault = INCLUDED in the workbook anyway,
  // because her spreadsheet carries Value Reduction and EffectiveTaxRate as standard columns —
  // unless the user has explicitly hidden them, in which case the export follows the screen.
  { key: 'reduction', label: 'Value Reduction', type: 'value', optional: true, exportDefault: true },
  { key: 'rate', label: 'EffectiveTaxRate', type: 'rate', optional: true, exportDefault: true },
  { key: 'taxes', label: 'Taxes', type: 'money', total: true },
  { key: 'savings', label: 'Savings', type: 'money', total: true },
  // PRO-RATA (Philip 2026-10-28): a shared parcel's row is the client's share (splitByProrata, below). These two say
  // which share and what the whole bill was. Off on screen, in the exports — and only when the report has a pro-rata
  // row; the full tax is then on every row (a parcel without pro-rata: its own Taxes). Never totalled: a parcel listed
  // under three properties would count its whole bill three times.
  { key: 'pro_rata_pct', label: 'Pro-rata %', type: 'percent', optional: true, exportDefault: true, showWhenUsed: true },
  { key: 'full_taxes', label: 'Full Tax (100%)', type: 'money', optional: true, exportDefault: true, showWhenUsed: 'pro_rata_pct' },
  // Optional everywhere, on screen AND in the workbook: hers carries Address1 and LocationCode,
  // ours are sparse. location_code is a real column on BOTH properties and parcel_year_data — the
  // property one has a UI (AddPropertyModal:239) and is displayed on the property screen
  // (StaffPortal:2093) — but whether either is actually populated for LaSalle is unverified, so it
  // ships off by default rather than as a column of blanks.
  { key: 'address', label: 'Address1', type: 'text', optional: true },
  { key: 'location_code', label: 'LocationCode', type: 'text', optional: true },
  // LEVELS SIDE BY SIDE (step 8): each level's value, tax and savings from Proposed, as if that level were final. All
  // optional — ticked in Columns — so a report's default output is what it always was.
  ...LEVEL_KEYS.flatMap((L) => [
    { key: `lvl_${L}_value`, label: `${appealLevelLabel(L)} Value`, type: 'value', optional: true },
    { key: `lvl_${L}_taxes`, label: `${appealLevelLabel(L)} Taxes`, type: 'money', optional: true },
    ...(L === 'proposed' ? [] : [{ key: `lvl_${L}_savings`, label: `${appealLevelLabel(L)} Savings`, type: 'money', optional: true }]),
  ]),
];

// One client or several (step 8: the Clients pick). Each row carries its client_id.
export async function fetchParcelYearScope({ clientIds, years, allYears }, label = 'report') {
  if (!clientIds?.length) return null;

  const props = await fetchAll('properties',
    'id,client_id,name,state,county,address,location_code,first_year,retired_year,reactivated_year', (q) => q.in('client_id', clientIds));
  if (!props.length) return null;
  const propIds = props.map((p) => p.id);

  const parcels = await fetchByIds('parcels',
    'id,property_id,parcel_name,account_number,assessor,assessor_ref_id,state,address,first_year,retired_year,reactivated_year,re_pp',
    'property_id', propIds);
  if (!parcels.length) return null;
  const ids = parcels.map((p) => p.id);

  // ⛔ tax_bills is fetched WITHOUT a year filter. The estimate rule needs the most recent PRIOR
  // year's rate when the reported year has none, so restricting to the selected years would make
  // every carried-forward figure blank instead of estimated.
  const [pyd, appeals, decisions, bills, jurRates, manual, references, links, nonNtrgRows, noticeRows, noticeTypes, prorata] = await Promise.all([
    fetchByParcelIds('parcel_year_data', 'parcel_id,tax_year,appeal_status', ids),
    // ⛔ is_primary IS IN THIS SELECT AND MUST STAY. primaryAppealRow reads it to choose between two taxable
    // bases; without it every row arrives with the key ABSENT, a two-row parcel-year resolves to null, and
    // Proposed / Final / Taxes / Savings all render as dashes with nothing erroring. That shipped, and
    // Boynton Beach 435200IN 2026 was the first two-row parcel to reach a report — one row per parcel-year
    // resolves regardless of the flag, so the whole database hid the defect. The helper now names this exact
    // cause rather than reporting ambiguous data.
    // ⛔ EVERY NOTICE IS READ; the builder splits them (step 8). Rows for the original notice (0) are built exactly as
    // before; an added notice's rows are built only when the Notices option asks for them.
    fetchByParcelIds('parcel_appeals', 'parcel_id,tax_year,notice_no,appeal_level,jurisdiction,is_primary,market_total,taxable_total,savings_baseline_for', ids),
    fetchByParcelIds('parcel_appeal_decisions', 'parcel_id,tax_year,notice_no,appeal_level,decision', ids),
    fetchByParcelIds('tax_bills', PARCEL_YEAR_BILL_COLS, ids),
    // Per-jurisdiction rates. Empty for every parcel until someone enters one, which is why the savings path
    // below falls back to the consolidated-rate arithmetic rather than blanking.
    fetchByParcelIds('parcel_jurisdiction_rates', 'parcel_id,tax_year,jurisdiction,tax_rate,rate_unit', ids),
    fetchByParcelIds('parcel_taxes', 'parcel_id,tax_year,proposed_tax,final_tax', ids),
    // Pre-appeal reference bills (utils/billSavings). Until 2026-10-09_tax_bill_references_apply.sql has run the
    // table does not exist; that one case reads as "none on file" so the reports keep working. Anything else throws.
    fetchByParcelIds('tax_bill_references', 'parcel_id,tax_year,jurisdiction_rates,total_ad_valorem,source', ids).catch((e) => {
      if (e && (e.code === '42P01' || e.code === 'PGRST205')) { console.warn('[reports] tax_bill_references not found — no references used'); return []; }
      throw e;
    }),
    // The parcel's collectors per year — billedYearTotal needs every one of them billed before it sums the year.
    fetchByParcelIds('parcel_collectors', 'parcel_id,tax_year,collector_id,active', ids),
    // Parcel-years whose (original notice's) Appeal One was a Non-NTRG appeal (step 7).
    fetchByParcelIds('parcel_non_ntrg', 'parcel_id,tax_year,notice_no', ids),
    // Added notices and their type names (the Notice column).
    fetchByParcelIds('parcel_notices', 'parcel_id,tax_year,notice_no,type_key', ids),
    fetchAll('notice_types', 'key,name'),
    // Pro-rata lines (splitByProrata): the client's property each counts toward, and the year when it is an override.
    fetchByParcelIds('parcel_prorata', 'parcel_id,property_id,tax_year,pro_rata_pct', ids),
  ]);
  // Those collectors' installment schedules (collector_installments is keyed on the collector ENTITY and the year).
  const schedule = await fetchByIds('collector_installments', 'collector_id,tax_year,kind,installment_number', 'collector_id',
    [...links.map((l) => l.collector_id), ...bills.map((b) => b.collector_id)]);

  // Which years to emit: the selection, or every year present in the data.
  const dataYears = [...new Set([...pyd, ...appeals].map((r) => String(r.tax_year)).filter(Boolean))].sort();
  const wantYears = allYears || !years?.length ? dataYears : years.map(String);

  // Representation gate, per year, cascaded parcel AND property, fail-open.
  const propRep = new Map(props.map((p) => [p.id, p]));
  const allowed = new Map();
  // Parcel-years we were NOT engaged in but which may carry the client's own prior figures.
  const baseline = new Map();
  wantYears.forEach((y) => {
    const g = gateParcels(parcels, propRep, y, label);
    allowed.set(y, new Set(g.rows.map((p) => p.id)));
    baseline.set(y, g.baselineIds);
  });

  // Assessor names resolve per YEAR — assessors is a per-year versioned table. The years are
  // INDEPENDENT of one another, so resolving them one await at a time was 6-12 serialised round
  // trips, more than the rest of this fetch combined. Bounded parallelism instead; each year still
  // resolves exactly as it did alone, and the Map is rebuilt in wantYears order.
  const resolved = await mapWithConcurrency(wantYears, (y) => resolveAssessorsForParcels(parcels, y));
  const assessorByYear = new Map(wantYears.map((y, i) => [y, resolved[i]]));

  // Non-NTRG ticks: the original notice's under the parcel-year key, an added notice's under parcel-year|notice.
  const nonNtrg = new Set(nonNtrgRows.map((r) => (r.notice_no ? `${key(r.parcel_id, String(r.tax_year))}|${r.notice_no}` : key(r.parcel_id, String(r.tax_year)))));
  const typeName = new Map(noticeTypes.map((t) => [t.key, t.name]));
  const noticeNames = new Map(noticeRows.map((n) => [`${key(n.parcel_id, String(n.tax_year))}|${n.notice_no}`, { key: n.type_key, name: typeName.get(n.type_key) || n.type_key }]));
  return { props, parcels, pyd, appeals, decisions, bills, references, links, schedule, jurRates, manual, nonNtrg, noticeNames, prorata, wantYears, allowed, baseline, assessorByYear };
}

// ═══ PRO-RATA — A SHARED PARCEL REPORTS THE CLIENT'S SHARE (Philip 2026-10-28) ═══
// The card's Pro-Rata Allocation (parcel_prorata) splits a parcel into lines. A line linked to one of the client's
// properties counts toward it; a line with no property (an airport's "No Rights", another occupant) is not the client's
// and is left out. A year's lines are its own override when it has one, else the parcel's every-year lines.
//   Airport lot   DTAG La Guardia 10% / No Rights 90%  → one row, La Guardia, at 10%
//   Asana         A 40% / B 35% / C 25%                → three rows, one under each property
// The client's total is the sum of its lines — never more than the bill. No lines, or one 100% line to the parcel's own
// property, = the row exactly as it always was. Bills, tax lines and the card stay as billed: only report rows scale.
// Which shares a parcel-year has: utils/prorata prorataShares (the Properties list and property card use it too).

// The fields a share scales: every value, reduction, tax and savings figure. Rates do not — 10% of a parcel is taxed at
// the parcel's rate. A Pending or n/m cell stays as it is; an estimated or recovered figure keeps its mark.
const SCALED = ['proposed_value', 'final_value', 'proposed_market', 'final_market', 'reduction', 'ntrg_reduction',
  'taxes', 'savings', 'billable_taxable',
  ...LEVEL_KEYS.flatMap((L) => [`lvl_${L}_value`, `lvl_${L}_taxes`, `lvl_${L}_savings`])];
const scaleBy = (v, f) => (typeof v === 'number' ? v * f
  : v && typeof v === 'object' && typeof v.value === 'number' ? { ...v, value: v.value * f } : v);

// One report row per share, under the share's property (props: id → property). Shares whose property is not in the
// report's scope are left out with the non-client lines.
export function splitByProrata(row, shares, props) {
  if (!shares) return [{ ...row, full_taxes: row.taxes }];
  return shares.filter((s) => props.has(s.property_id)).map((s) => {
    const p = props.get(s.property_id);
    const out = { ...row, __key: `${row.__key}|${s.property_id}`, property_id: s.property_id, property: p.name || s.property_id,
      client_id: p.client_id || row.client_id, location_code: p.location_code || '', pro_rata_pct: s.pct, full_taxes: row.taxes };
    SCALED.forEach((k) => { if (k in row) out[k] = scaleBy(row[k], s.pct / 100); });
    return out;
  });
}

// Rate lines for (parcel, year) from that parcel's bills, and the carried-forward fallback the estimate flag rides on.
function rateContext(billsByYear, year, label) {
  const own = pickRateLines(billsByYear.get(String(year)), label);
  if (own) return { lines: own, estimated: false, sourceYear: String(year) };
  const priors = [...billsByYear.keys()].filter((y) => y < String(year)).sort((a, b) => b.localeCompare(a));
  for (const y of priors) {
    const l = pickRateLines(billsByYear.get(y), label);
    if (l) return { lines: l, estimated: true, sourceYear: y };
  }
  return { lines: null, estimated: false, sourceYear: null };
}

// One value figure per level: the only column, or the single one marked primary (utils/appealRows).
function primaryLevels(lvlRows, label) {
  const out = {};
  Object.entries(lvlRows || {}).forEach(([level, rows]) => {
    const row = primaryAppealRow(rows, `${label} ${level}`);
    out[level] = { market: row ? toNumber(row.market_total) : null, taxable: row ? toNumber(row.taxable_total) : null };
  });
  return out;
}

// The base a two-base year's single Proposed / Final / Reduction columns describe: the final column with the LARGEST
// reduction from its start column of the same name. → { start, final, jurisdiction } as { market, taxable }, or null
// when the final level has one column or no base came down (the caller keeps the primary). A tie keeps the first
// reduced base unless the primary is among the tied.
function largestReductionBase(startRows, finalRows) {
  if ((finalRows || []).length < 2) return null;
  const vals = (r) => ({ market: toNumber(r.market_total), taxable: toNumber(r.taxable_total) });
  let best = null;
  finalRows.forEach((f) => {
    const s = sameJurisdictionRow(startRows, f.jurisdiction);
    const red = s && toNumber(s.taxable_total) != null && toNumber(f.taxable_total) != null
      ? toNumber(s.taxable_total) - toNumber(f.taxable_total) : null;
    if (red == null || red <= 0) return;
    if (!best || red > best.red || (red === best.red && f.is_primary === true)) {
      best = { start: vals(s), final: vals(f), red, jurisdiction: f.jurisdiction };
    }
  });
  return best;
}

// ═══ ONE PARCEL-YEAR'S FIGURES — the reports AND the parcel card call this, so they cannot disagree ═══
// (Philip, 2026-10-08: "The card and reports should never show different savings for the same parcel-year.")
// Pure: everything comes in as arguments. The reports pass what they fetched; the card passes its live state, so
// it shows what the reports will show once saved.
//   lvlRows      { [appeal_level]: parcel_appeals rows }        decisions  { [appeal_level]: 'accepted'|'withdrawn'|'denied' }
//   status       the year's Appeal Status                       savedRates { [jurisdiction]: parcel_jurisdiction_rates row }
//   manual       { proposed, final } hand-entered tax, or null  billsByYear Map(year → this parcel's tax_bills)
// → display fields (reduction / savings read PENDING until the outcome is final) plus `card`, the raw figures.
//   billedTotal  billedYearTotal for this parcel-year, or null (the reports pass it; the card shows no Taxes figure)
export function parcelYearFigures({ lvlRows = {}, decisions, status, savedRates, manual, billsByYear = new Map(), referencesByYear = new Map(), year, state, label, basis = 'taxable', billedTotal = null, nonNtrg = false }) {
  // Per-jurisdiction rates a TS entered for this parcel-year. Used by BOTH the estimated-tax path and
  // the savings path below — one lookup, so the two cannot disagree about which rates exist.
  // ⛔ EVERY YEAR (Philip 2026-10-27, reversing 10-08's two-base-only rule): the card shows the Tax Rate row on every
  // year and its Taxes-tab rate cell is the same stored rate, so a rate typed from a notice counts here too. A bill's
  // rate still wins — an entered rate only reaches a year with no bill of its own (the tiers below).
  const jrForYear = savedRates || {};

  const levels = primaryLevels(lvlRows, label);
  const proposed = levels.proposed || { market: null, taxable: null };
  // ⛔ THE FINAL LEVEL IS THE SHARED RULE (utils/finalLevel — Philip, 2026-10-08): the bottom-most level with
  // values, a withdrawn level skipped. A decision no longer picks the level; the year's Appeal Status says
  // whether the answer is FINAL (reduction and savings count) or PENDING (they print "Pending").
  const fin = resolveFinalLevel({ levels: lvlRows, decisions: decisions || {}, status: status ?? null });
  const finalLevel = fin.level;
  const isFinal = fin.outcome === 'final';
  // Final on Proposed (Value Accepted with no appeal, or the appeal withdrawn back to it): nothing came off —
  // reduction 0 and savings $0, no rate needed.
  const noResult = !finalLevel || finalLevel === 'proposed';
  const unchanged = isFinal && noResult;
  const finalVals = (finalLevel && levels[finalLevel]) || proposed;

  // Display basis follows the toggle; the REDUCTION that savings is computed from is always
  // TAXABLE — that is the base the consolidated ad valorem rate actually applies to.
  const pick = (v) => (basis === 'market' ? v.market ?? v.taxable : v.taxable ?? v.market);
  const proposedValue = pick(proposed);
  const finalValue = pick(finalVals);
  // ⛔ COMPUTED WHETHER OR NOT THE OUTCOME IS FINAL — the card shows a pending figure labelled "Pending — not final"
  // (Philip, 2026-10-08); the reports print PENDING instead (see the display fields at the end).
  const reductionTaxable = noResult ? (unchanged ? 0 : null)
    : (proposed.taxable != null && finalVals.taxable != null) ? proposed.taxable - finalVals.taxable : null;

  // A filed $0 bill is this year's answer (zeroBilledYear): never a prior year's rate, never an estimate.
  const zeroBilled = zeroBilledYear(billsByYear.get(String(year)));
  const ctx = rateContext(billsByYear, year, label);
  const { lines, estimated, sourceYear } = zeroBilled && ctx.estimated ? { lines: null, estimated: false, sourceYear: String(year) } : ctx;
  const split = lines ? splitJurisdictionLines(lines) : null;
  const printedTotal = split && !estimated ? printedOverLines(billsByYear.get(String(year)), lines, split.total, billedTotal) : null;
  // ⛔ `total: true`. split.sumRate is Σ over EVERY rated line on the parcel — a whole-parcel
  // consolidated rate — so the tight 0.5%–5% aggregate band is the one that applies. It was
  // being judged by the WIDE per-line band (0.01%–10%), which describes a single jurisdiction's
  // component and exists only to catch millage stored without the /10. The wrong flag, not a
  // missing check.
  //
  // What it cost: LA parcel 8940-436-179 / 2021 lost its 1.000000% general tax levy when the
  // extractor read a wrapped unit name ("GENERAL TAX LEVY" / "ALL AGENCIES") as the fragment
  // "ALL AGENCIES" and dropped the rate with the first line. Σ of the surviving rates is
  // 0.175188% — nonsense as a whole-parcel rate, unremarkable as one line's. It was accepted,
  // printed in the rate column, multiplied into savings, and carried forward as the estimating
  // rate for every later year of that parcel. One flag turns all of that into a blank.
  // ⛔ THE LINES' UNIT, ASKED OF THE LINES. Florida (and Alabama, Georgia, Missouri, Minnesota — most of
  // the portfolio still to import) quotes MILLAGE per line while total_tax_rate holds the converted
  // percent. This layer assumed percent, so Σ of a Florida bill's ten lines came to 13.4686, which
  // normaliseRate read as 13.47% — outside the 0.5%–5% whole-parcel band — and rateRefused then
  // suppressed Taxes, Savings AND Rate together. Four RecNation parcels and every millage state.
  // resolveRateUnit reconciles each line against its own base; 'millage' is a source normaliseRate has
  // understood since SigerTax, so nothing there changes.
  const rateUnit = split ? resolveRateUnit(lines) : null;
  const millage = !!(rateUnit && rateUnit.confident && rateUnit.divisor === 1000);
  const consolidated = split && split.sumRate != null
    ? normaliseRate(split.sumRate, { state, context: label, total: true, source: millage ? 'millage' : null }) : null;

  // ⛔ EVERY RECOVERY IS LOGGED, whether or not the band goes on to accept it. A rate this
  // layer reconstructed is not a rate the county printed, and the trail has to say which line,
  // what the scanner actually wrote there, and the arithmetic — enough to check it against the
  // paper bill without running a query.
  (split?.recovered || []).forEach((r) => console.warn(
    `[reports/taxRate] ${label}: RECOVERED a lost rate on "${r.name}". The rate cell held `
    + `${JSON.stringify(r.rawRate)}, which is not a rate, but the line carries a taxable value — `
    + 'something a flat direct assessment never does — so it is a rated line whose rate was lost, '
    + 'most likely to a unit name wrapped across two printed lines. '
    + `$${r.amount} / ${r.taxableValue} = ${r.impliedRate.toFixed(6)}%. `
    + `Σ with it is ${split.sumRate}%, which the band ${consolidated != null ? 'ACCEPTED' : 'REFUSED'}. `
    + 'Read-time only — the stored bill is untouched.'
  ));

  // The value the county actually levies on. While an appeal is open you pay on the ASSESSOR'S
  // value, not on the number we are asking for — so the billable base is the proposed value
  // until the outcome is final, at which point the final value is what is billed.
  const billableTaxable = (isFinal ? finalVals.taxable : proposed.taxable) ?? proposed.taxable;

  // ⛔ TAXES. Two paths, and the difference is the whole point of the asterisk.
  //
  // ACTUAL year — Σ amount over every line of this year's own bill. Transcribed figures, no
  // arithmetic.
  //
  // ESTIMATED year — A RATE IS APPLIED TO THIS YEAR'S VALUE; THE DOLLAR FIGURE IS NEVER CARRIED.
  // The rate is the TS's own per-jurisdiction entry when one exists (tier 2 below), else the prior
  // year's consolidated rate. Either way the figure is computed from THIS year's value. Applying the prior
  // year's consolidated ad valorem rate to THIS year's value, and carrying the prior year's
  // FIXED charges across unchanged, because a flat direct assessment does not scale with
  // assessed value — a mosquito-abatement charge is the same on a $60M parcel as on a $63M
  // one. Copying last year's total instead would print an identical figure two years running
  // while the value underneath moved, which is exactly what it did before this was fixed.
  //
  // Blank, never a number, when the rate or the value is missing: if the rate cannot be
  // applied there is no estimate to make, and carrying the dollars would be the bug again.
  // ⛔ AND THE SAME REFUSAL GOVERNS THE ACTUAL-YEAR FIGURE, which does not otherwise touch
  // `consolidated` at all. Σ amount over the lines is only defensible if the lines ARE the
  // whole bill, and the consolidated rate is the only evidence of that this layer holds.
  // 8940-436-179's total was RIGHT — $1,839.58, to the cent — while its rate was 85% wrong,
  // because the lost levy kept its amount and lost only its rate. A total that happens to be
  // correct is indistinguishable from one missing a line, so a bill whose rate we refuse is a
  // bill whose total we decline to publish.
  //
  // NO rated lines at all is a different condition and is left alone: a bill of pure direct
  // assessments has no ad valorem component to check, and its total stands.
  const rateRefused = !zeroBilled && !!split && split.rated.length > 0 && consolidated == null;   // a $0 bill needs no rate
  if (rateRefused) {
    console.warn(
      `[reports/parcelYearRows] ${label}: SUPPRESSING Taxes, Savings and Rate. The consolidated `
      + `rate Σ ${split.sumRate}% across ${split.rated.length} rated line(s)`
      + `${estimated ? ` — carried forward from ${sourceYear} —` : ''} was refused as implausible, `
      + `so the ${estimated ? 'estimate built on it' : `Σ $${(split.total ?? 0).toFixed(2)} from this year's own bill`} `
      + 'cannot be trusted either. Read the bill: the likeliest cause is a rated line that lost '
      + 'its rate to a unit name wrapped across two printed lines, keeping its amount. '
      + `Fixed lines ${split.fixed.length}, rated Σ $${(split.sumRatedAmount ?? 0).toFixed(2)}.`
    );
  }

  // ⛔ TIER 2 — RATES A TS ENTERED OUTRANK A RATE INFERRED FROM AN OLDER DOCUMENT, but neither outranks
  // the year's OWN BILL. Three tiers, and the order is the precedence used everywhere else:
  //   1. the year has its own bill      -> split.total, the county's transcribed line amounts. Wins
  //      outright: a billed figure is authoritative, an entered rate is for projecting an unbilled year.
  //   2. no bill, rates entered          -> Σ(base_j × rate_j), each base at its own rate
  //   3. no bill, no rates               -> last year's consolidated rate × this year's base (was the
  //      only behaviour, and gave 435200IN 2026 $219,048.06 off 2025's 2.0008% while the parcel card
  //      computed ~$223,382 from the entered 1.3675% / 0.6287%. Same parcel, two answers.)
  //
  // ⛔ THE PRIOR YEAR'S FIXED LINES ARE STILL ADDED IN TIER 2. Per-jurisdiction rates cover AD VALOREM
  // only, so Σ(base × rate) alone would silently drop flat direct assessments — a fire assessment or
  // garbage charge is still owed and does not scale with value. Boynton's TRIM carries 645.00 of
  // non-ad-valorem for exactly that reason, and dropping it would understate the estimate by a real
  // amount. The row stays asterisked either way: there is no bill for the year, whatever the rate's
  // provenance.
  //
  // ⛔ PARTIAL REFUSES. A parcel with a rate on one base and not the other would otherwise be taxed on
  // one authority's levy and presented as the year's estimate; aggregateTax reports that as partial.
  // Tier 3 then answers instead, which is a worse estimate but not a wrong one.
  const billableRows = lvlRows[(isFinal && finalLevel) ? finalLevel : 'proposed'] || [];
  // A column's rate is its own Tax Rate row entry, else its marked baseline's (columnRateRow) — the card's rule.
  const rateOfCol = (j) => ratePercentOf(columnRateRow(j, jrForYear, lvlRows.proposed));
  const enteredTax = billableRows.length && billableRows.some((r) => rateOfCol(r.jurisdiction) != null)
    ? aggregateTax(billableRows.map((r) => ({
        jurisdiction: r.jurisdiction, taxableValue: toNumber(r.taxable_total), rate: rateOfCol(r.jurisdiction),
      })), { context: `${label} entered rates`, state })
    : null;
  const enteredAdValorem = (enteredTax && enteredTax.tax != null && !enteredTax.partial) ? enteredTax.tax : null;

  // The order: Final Tax $ (below), a filed $0 bill, this year's own lines, this year's BILLED TOTAL (billedYearTotal —
  // only when the year has no lines of its own), then the prior-year estimate. The billed total is an actual bill, so
  // it carries no estimate mark.
  const useBilledTotal = !zeroBilled && billedTotal != null && !(lines && !estimated);
  const billedTaxes = zeroBilled ? 0
    : useBilledTotal ? billedTotal
    : !split || rateRefused ? null
    : !estimated ? printedTotal ?? split.total
    : enteredAdValorem != null ? enteredAdValorem + (split.sumFixedAmount ?? 0)
    : (consolidated != null && billableTaxable != null)
      ? billableTaxable * consolidated + (split.sumFixedAmount ?? 0)
      : null;

  // SAVINGS — reduction × consolidated RATED rate. Fixed charges are excluded because they do
  // not move when an appeal succeeds. normaliseRate applies the sanity band and returns null
  // rather than a figure that is likely 10x wrong.
  // ═══ SAVINGS ACROSS TWO TAXABLE BASES ═══
  // ⛔ `reductionTaxable × consolidated` IS WRONG THE MOMENT A PARCEL HAS TWO BASES, TWICE OVER.
  // reductionTaxable is the PRIMARY base's reduction only, and `consolidated` is Σ of EVERY rated bill
  // line — school plus non-school added together — a rate that applies to neither base. On a Florida
  // parcel over the assessment cap that multiplies one base's reduction by both authorities' rates.
  //
  // With a rate stored per jurisdiction, base and rate sit on the same row and savings is
  // Σ(Δbase_j × rate_j) — aggregateSavings, which values BOTH sides at the same per-jurisdiction rates
  // and returns null unless both compute. Falls back to the consolidated arithmetic when no rate is
  // stored, which is every parcel today, so nothing moves until someone enters one.
  const pRows = lvlRows.proposed || [];
  const fRows = (finalLevel && lvlRows[finalLevel]) || pRows;
  const perJurisdiction = (!noResult && pRows.length > 1 && pRows.some((r) => ratePercentOf(jrForYear[r.jurisdiction]) != null))
    ? aggregateSavings(
        pRows.map((r) => ({ jurisdiction: r.jurisdiction, taxableValue: toNumber(r.taxable_total), rate: ratePercentOf(jrForYear[r.jurisdiction]) })),
        pRows.map((pr) => {
          // ⛔ MATCHED BY JURISDICTION, NEVER BY POSITION. Taking each level's rows in order could
          // subtract the non-school final from the school proposed and report the assessment cap as a
          // reduction we had won.
          const f = fRows.find((x) => x.jurisdiction === pr.jurisdiction);
          return { jurisdiction: pr.jurisdiction, taxableValue: f ? toNumber(f.taxable_total) : null, rate: ratePercentOf(jrForYear[pr.jurisdiction]) };
        }),
        { context: label })
    : null;
  // ⛔ A PARTIAL SAVING IS NOT A SAVING — one jurisdiction missing its rate understates the figure, and
  // this column is invoiced against. aggregateSavings reports partial; we refuse on it.
  // ⛔ A MARKED SAVINGS BASELINE OVERRIDES ALL OF THE ABOVE (utils/baselineSavings — Philip, 2026-10-08). A
  // Proposed column marked "savings baseline for F" is what F's saving is measured from (a revised notice, so
  // the assessor's own drop is not claimed), each base at its own rate: this year's bill lines grouped to the
  // final columns, else the Tax Rate row. Never the consolidated rate. Final years with a reduction only.
  // No marker on the parcel-year → null, and every figure below is exactly what it was.
  const marked = !noResult
    ? baselineSavings({ proposedRows: pRows, finalRows: fRows,
      rateFor: baseRateSource({ billLines: pickRateLines(billsByYear.get(String(year)), label), finalRows: fRows, savedRates: jrForYear, year }) })
    : null;
  // ⛔ THE BILLS' OWN CENTS WIN (utils/billSavings — Philip, 2026-10-09). The county rounds each line per bill, so
  // where this year's bills can be placed on the baseline and final values the saving is read off them: both bills
  // → original − revised as printed (B); one bill → per-line rounding (A). Otherwise the methods above stand.
  // Pairs are the bases the saving is measured between: the marked baselines, else each final column and the
  // Proposed column of the same name, else a lone Proposed column and a lone final column.
  const pairs = noResult ? null
    : marked ? marked.bases.map((b) => ({ baseline: b.baseline, final: b.finalValue }))
    : (pRows.length === 1 && fRows.length === 1) ? [{ baseline: toNumber(pRows[0].taxable_total), final: toNumber(fRows[0].taxable_total) }]
    : fRows.map((f) => { const p = pRows.find((x) => x.jurisdiction === f.jurisdiction);
      return { baseline: p ? toNumber(p.taxable_total) : null, final: toNumber(f.taxable_total) }; });
  const fromBills = pairs ? billSavings({ bills: billsByYear.get(String(year)), references: referencesByYear.get(String(year)), pairs }) : null;

  // The single-figure Proposed / Final / Reduction columns describe ONE base: the one with the largest reduction,
  // so Proposed − Final = Reduction still holds on the row (else the final level's primary column).
  const head = marked && marked.headline;
  const headRow = (rows, name) => (rows || []).find((r) => r.jurisdiction === name) || null;
  const headVals = (r) => ({ market: r ? toNumber(r.market_total) : null, taxable: r ? toNumber(r.taxable_total) : null });

  // Only the consolidated path uses a rate that can be a PRIOR year's (rateContext), so only it carries the
  // "Estimated (prior-year rate)" mark — marked and per-jurisdiction savings use this year's bill or entered rates.
  const viaConsolidated = !noResult && !fromBills && !marked && !perJurisdiction;
  const derivedSavings = noResult ? (unchanged ? 0 : null)
    : fromBills ? fromBills.total
    : marked ? marked.total
    : perJurisdiction
      ? (perJurisdiction.partial ? null : perJurisdiction.savings)
      : ((reductionTaxable != null && consolidated != null) ? reductionTaxable * consolidated : null);

  // 4h: the hand-entered fields are an OVERRIDE that wins when populated. They are empty
  // across the entire database — nobody was ever meant to fill them — so the report must be
  // fully correct with them untouched, and nothing here requires them.
  // ⛔ NON-NTRG APPEAL ONE (step 7, Philip 2026-10-21): someone other than NTRG appealed at Appeal One. Its values
  // stay, the Value Reduction column stays Proposed → final, but NTRG's savings — and its realised reduction
  // (ntrgReduction) — start at Appeal One's result: $0 when Appeal One is final, Appeal One → final when the parcel
  // went further (NOT $0). Measured by the per-jurisdiction rates or the consolidated rate only: the hand-entered
  // Proposed tax, the savings-baseline markers and pre-appeal reference bills are all measured from Proposed, so
  // none of them can be the start point (Philip, decision 3).
  const a1Rows = lvlRows.appeal_one || [];
  const nonNtrgApplies = nonNtrg && !noResult && a1Rows.length > 0 && levelOrdinal(finalLevel) >= 1;
  const a1Final = nonNtrgApplies && finalLevel === 'appeal_one';
  const a1 = levels.appeal_one || { market: null, taxable: null };
  const ntrgReduction = !nonNtrgApplies ? null : a1Final ? 0
    : (a1.taxable != null && finalVals.taxable != null) ? a1.taxable - finalVals.taxable : null;
  const ntrgPerJur = nonNtrgApplies && !a1Final && a1Rows.length > 1 && a1Rows.some((r) => ratePercentOf(jrForYear[r.jurisdiction]) != null)
    ? aggregateSavings(
        a1Rows.map((r) => ({ jurisdiction: r.jurisdiction, taxableValue: toNumber(r.taxable_total), rate: ratePercentOf(jrForYear[r.jurisdiction]) })),
        a1Rows.map((ar) => {
          const f = fRows.find((x) => x.jurisdiction === ar.jurisdiction);
          return { jurisdiction: ar.jurisdiction, taxableValue: f ? toNumber(f.taxable_total) : null, rate: ratePercentOf(jrForYear[ar.jurisdiction]) };
        }),
        { context: `${label} non-NTRG Appeal One` })
    : null;
  const ntrgViaConsolidated = nonNtrgApplies && !a1Final && !ntrgPerJur;
  const ntrgSavings = !nonNtrgApplies ? null : a1Final ? 0
    : ntrgPerJur ? (ntrgPerJur.partial ? null : ntrgPerJur.savings)
    : (ntrgReduction != null && consolidated != null) ? ntrgReduction * consolidated : null;

  // ═══ WHICH BASE THE SINGLE PROPOSED / FINAL / REDUCTION COLUMNS DESCRIBE (Philip 2026-10-22) ═══
  // Display only — savings above are untouched. The start is NTRG's: Appeal One on a Non-NTRG row with the switch off
  // (so every Non-NTRG row reads Appeal One → final, one base or two), else Proposed. With 2+ final columns, the base
  // with the largest reduction; none reduced, the primary columns as before. A marked savings baseline keeps its own
  // headline (head). proposed_market / final_market stay on the primary base: Debbie's Tax Savings Appeal Summary
  // computes her savings from them (decision 3).
  const shown = head || noResult ? null
    : largestReductionBase(nonNtrgApplies ? a1Rows : pRows, fRows)
      || { start: nonNtrgApplies ? a1 : proposed, final: finalVals, jurisdiction: null };
  const shownReduction = shown && shown.start.taxable != null && shown.final.taxable != null
    ? shown.start.taxable - shown.final.taxable : null;

  const man = manual;
  const taxes = man?.final != null ? man.final : billedTaxes;
  // The year's supplementals on top of its regular Taxes — after every rate above, which reads the regular figure only.
  // Not on a hand-entered Final Tax $ (it is the year's tax as typed), nor when the regular figure is blank (a partial
  // year printed as the year's tax is worse than a blank).
  const suppTotal = supplementalYearTotal(billsByYear.get(String(year)));
  const withSupp = (v) => (v == null || suppTotal == null || man?.final != null ? v : Math.round((v + suppTotal) * 100) / 100);
  const fromEntered = !nonNtrgApplies && man?.proposed != null && man?.final != null;
  const savings = nonNtrgApplies ? ntrgSavings : fromEntered ? man.proposed - man.final : derivedSavings;

  // ⛔ THE RECOVERED MARKER, and it is NOT the estimate asterisk. They say different things and
  // conflating them would be worse than showing neither:
  //   *  ESTIMATED — this year has no bill of its own, so a prior year's rate was applied to
  //      this year's value. The rate is real; the year it came from is not this one.
  //   †  RECOVERED — this year's own bill was read, but a line lost its printed rate and this
  //      layer reconstructed it from the bill's own arithmetic. The year is right; the rate is
  //      derived rather than transcribed.
  // A figure can be both, and then it carries both marks.
  //
  // Only surfaced when the band ACCEPTED the result: a refused recovery blanks the figure
  // entirely, so there is nothing left to mark. Suppressed under a hand-entered override for
  // the same reason the estimate flag is — an entered figure did not come from the bill.
  const rateRecovered = !!split?.recovered?.length && consolidated != null;
  // ⛔ A YEAR WITH A BILL SHOWS THE BILL'S RATE (Philip 2026-10-27): its own lines' (consolidated, when they are this
  // year's), else its printed total, else its EFFECTIVE rate (ad valorem ÷ taxable, marked effective) — else a rate
  // entered for the year. Never a carried one. A bill whose lines were refused stays blank, as its Taxes do; a filed
  // $0 bill keeps the rule below. Taxes and savings don't read this — it is the rate column only.
  const yearBill = zeroBilled ? null : pickYearBill(billsByYear.get(String(year)));
  const enteredRate = billableRows.length === 1 ? rateOfCol(billableRows[0].jurisdiction) : null;
  const billRate = !yearBill ? null
    : (consolidated != null && !estimated) ? (rateRecovered ? { value: consolidated * 100, recovered: true } : consolidated * 100)
    : printedBillRate(yearBill, { state, context: label })
      ?? (() => {
        const e = effectiveBillRate({ adValorem: yearAdValorem(billedTotal, yearBill), taxable: toNumber(yearBill.taxable_value) ?? billableTaxable, state, context: label });
        return e == null ? null : { value: e, effective: true };
      })();
  // The year's ACTUAL rate and where it came from (the Tax Rates report): the bill's, else an entered one. Never carried
  // or estimated — a year with neither has none.
  const actualRate = yearBill ? (rateRefused ? null : billRate ?? enteredRate) : enteredRate;
  // ═══ THE STATE EQUALIZER (Cook / Illinois — Philip 2026-10-30) ═══ The bill prints its Local Tax Rate, applied to the
  // EQUALIZED value (assessed × equalizer). With the year's bill carrying one, the rate shown is that printed rate (no
  // derived combined rate, no ‡) and anything that applies the rate to an assessed value multiplies by it too. Taxes and
  // savings never read it: they come from the bill.
  const equalizer = yearBill ? toNumber(yearBill.equalization_factor) : null;
  const rateSource = actualRate == null ? null
    : yearBill && billRate != null ? (billRate.effective ? 'Effective' : 'Bill') : 'Entered';
  const flag = (v) => {
    if (v == null) return null;
    const isEst = estimated && man?.final == null;
    const isRec = rateRecovered && man?.final == null;
    if (!isEst && !isRec) return v;
    return { value: v, ...(isEst ? { estimated: true } : {}), ...(isRec ? { recovered: true } : {}) };
  };

  return {
    finalLevel, outcome: fin.outcome, isFinal, unchanged, head,
    shownBase: shown ? shown.jurisdiction : null,   // the base the single columns describe, when picked by reduction
    primaryFinalValue: finalValue,                  // the final level's primary column, whatever base is shown
    proposed_value: head ? pick(headVals(headRow(pRows, head.baselineColumn))) : shown ? pick(shown.start) : proposedValue,
    final_value: head ? pick(headVals(headRow(fRows, head.final))) : shown ? pick(shown.final) : finalValue,
    // Market whatever the basis — the Tax Savings Appeal Summary works on market values (step 8 part 5b).
    proposed_market: head ? headVals(headRow(pRows, head.baselineColumn)).market : proposed.market,
    final_market: head ? headVals(headRow(fRows, head.final)).market : finalVals.market,
    reduction: !isFinal ? PENDING : head ? head.reduction : shown ? shownReduction : reductionTaxable,
    // NTRG's part of the reduction (Appeal Results' realised): the shown base's reduction, which on a Non-NTRG row
    // already starts at Appeal One.
    ntrg_reduction: !isFinal ? PENDING : head ? (nonNtrgApplies ? ntrgReduction : head.reduction) : shown ? shownReduction : reductionTaxable,
    non_ntrg: !!nonNtrg,
    taxes: useBilledTotal ? withSupp(taxes) : flag(withSupp(taxes)),   // an actual bill's total (or Final Tax $): never marked
    savings: !isFinal ? PENDING : (nonNtrgApplies ? ntrgViaConsolidated : viaConsolidated) ? flag(savings) : savings,
    // A year with a bill: the bill's rate (billRate, above). Without one: an entered rate, else a prior year's lines,
    // else Taxes ÷ Final (Philip, 2026-10-14), which carries Taxes' estimate mark.
    rate: marked ? null
      : yearBill ? actualRate
      : enteredAdValorem != null ? enteredRate
      : consolidated != null
        ? (rateRecovered ? { value: consolidated * 100, recovered: true } : consolidated * 100)
        : effectiveRate(useBilledTotal ? taxes : flag(taxes), head ? pick(headVals(headRow(fRows, head.final))) : finalValue),
    actualRate, rateSource, equalizer,
    billableTaxable,   // the value the year is taxed on: the final level's taxable once final, else Proposed's
    // For the parcel card: the same savings as a number, pending or not, and where its rate came from.
    card: {
      savings, marked: nonNtrgApplies ? null : marked, fromBills: nonNtrgApplies ? null : fromBills, nonNtrg: nonNtrgApplies,
      fromEntered,   // the hand-entered Appeal Tax $ (Proposed − Final) set the savings, over every other method
      viaPriorRate: (nonNtrgApplies ? ntrgViaConsolidated : viaConsolidated) && estimated && manual?.final == null,
      ratePct: (nonNtrgApplies ? ntrgViaConsolidated : viaConsolidated) && consolidated != null ? consolidated * 100 : null, rateYear: sourceYear,
    },
  };
}

export function buildParcelYearRows(raw, ctx) {
  if (!raw) return [];
  const { props, parcels, pyd, appeals, decisions, bills, references = [], links = [], schedule = [], jurRates, manual, nonNtrg = new Set(), noticeNames = new Map(), prorata = [], wantYears, allowed, baseline, assessorByYear } = raw;
  const basis = ctx?.filters?.basis || 'taxable';
  // The report options (step 8). Their defaults reproduce the reports exactly as they were.
  // Which notices become rows: 'current' (each year's value — a Corrected notice replacing the original, below),
  // 'original' (as entered), notice type keys, or 'all' (the original and every added notice, as entered).
  // Nothing chosen = Current.
  const pickedNotices = Array.isArray(ctx?.filters?.notices) && ctx.filters.notices.length ? ctx.filters.notices : ['current'];
  const noticePicked = (typeKey) => pickedNotices.includes('all') || pickedNotices.includes(typeKey);
  const wantCurrent = pickedNotices.includes('current');
  const nameNotices = !(pickedNotices.length === 1 && ['current', 'original'].includes(pickedNotices[0]));   // the Notice column, when more than one kind
  const includeNonNtrg = ctx?.filters?.nonNtrg === 'include';         // count a Non-NTRG Appeal One's savings as NTRG's
  const projectPending = ctx?.filters?.projected === 'include';       // undecided rows show the savings so far, projected
  // The Tax Savings Report's Level of Appeal (Philip 2026-10-29): a parcel-year that went past this level is read as if
  // this level were final (its levels up to it, decided). One that stopped at or before it is read exactly as it is.
  const throughLevel = ctx?.filters?.throughLevel || null;
  const propById = new Map(props.map((p) => [p.id, p]));
  const parcelById = new Map(parcels.map((p) => [p.id, p]));

  const statusMap = new Map(pyd.map((r) => [key(r.parcel_id, String(r.tax_year)), r.appeal_status]));

  // ⛔ THE VALUES ARE NO LONGER SUMMED ACROSS JURISDICTIONS. They were, and the comment here said so as
  // though it followed from the parcel-year grain — but jurisdiction rows are ALTERNATIVE valuations of one
  // property, not partitions of it. A Florida parcel with a school base of 11,778,423 and a non-school base
  // of 10,919,985 (the two differing by the capped 858,438) reported 22,698,408: a property that does not
  // exist, in the taxable column of four client-facing reports, feeding `taxes` and `savings`, erroring
  // nothing. See src/utils/appealRows.js for why is_primary decides and why ambiguity blanks.
  //
  // ROWS ARE COLLECTED PER LEVEL FIRST, then reduced once — the primary cannot be chosen row-by-row inside
  // a forEach, because it is a property of the whole group.
  const rowsByLvl = new Map();
  // Jurisdictions present on each parcel-year — WHICH authorities appear, so a report that groups by
  // jurisdiction has it without a second fetch. Unchanged: this was always a set, never a sum.
  const jurMap = new Map();
  // An added notice's levels, keyed parcel-year|notice (step 8). The original notice stays in rowsByLvl as before.
  const noticeLvls = new Map();
  appeals.forEach((r) => {
    if (r.notice_no) {
      const nk = `${key(r.parcel_id, String(r.tax_year))}|${r.notice_no}`;
      const byLvl = noticeLvls.get(nk) || {};
      (byLvl[r.appeal_level] = byLvl[r.appeal_level] || []).push(r);
      noticeLvls.set(nk, byLvl);
      return;
    }
    const k = key(r.parcel_id, String(r.tax_year));
    const byLvl = rowsByLvl.get(k) || {};
    (byLvl[r.appeal_level] = byLvl[r.appeal_level] || []).push(r);
    rowsByLvl.set(k, byLvl);
    if (r.jurisdiction) { const set = jurMap.get(k) || new Set(); set.add(r.jurisdiction); jurMap.set(k, set); }
  });
  // (parcel|year) -> { [jurisdiction]: rate row }. The rate lives at the SAME grain as the base, so no
  // matching of bill-line names to jurisdiction names is needed — that was the blocker that kept
  // aggregateTax unwired, and storing the rate beside the base removes it rather than solving it.
  const rateByJur = new Map();
  (jurRates || []).forEach((r) => {
    const k = key(r.parcel_id, String(r.tax_year));
    const m = rateByJur.get(k) || {};
    m[r.jurisdiction] = r;
    rateByJur.set(k, m);
  });


  // Every decision, not just the accepted ones — a report on decision OUTCOMES needs the others too ('accepted' and
  // 'withdrawn' are written; the row is deleted on recall). decByLvl feeds the final-level rule, which skips a
  // withdrawn level and reads an accepted one when the year has no Appeal Status.
  // ⛔ THE ROW'S DECISION IS THE FINAL LEVEL'S (step 8). It used to be whichever decision row the database returned
  // last, so a year with decisions on two levels could report either. Now: the final level's decision, else the
  // deepest level that has one. decByLvl (per level) feeds the final-level rule as before. Keyed per notice.
  const decByLvl = new Map();
  decisions.forEach((r) => {
    const k = r.notice_no ? `${key(r.parcel_id, String(r.tax_year))}|${r.notice_no}` : key(r.parcel_id, String(r.tax_year));
    decByLvl.set(k, { ...(decByLvl.get(k) || {}), [r.appeal_level]: r.decision });
  });
  const rowDecision = (dec, finalLevel) => {
    const d = dec || {};
    const level = (finalLevel && d[finalLevel]) ? finalLevel
      : Object.keys(d).filter((l) => d[l]).sort((a, b) => levelOrdinal(b) - levelOrdinal(a))[0] || null;
    return { decision: level ? d[level] : null, level };
  };
  // An added notice has no Appeal Status of its own (the year's is the original's): it is read off its levels and
  // decisions — an appealed, undecided notice reads as an open appeal (Philip, step 6 decision 3).
  const noticeStatus = (lvls, dec) => {
    const deepest = Object.keys(lvls).filter((l) => (lvls[l] || []).length).sort((a, b) => levelOrdinal(b) - levelOrdinal(a))[0];
    const d = dec || {};
    if (deepest && levelOrdinal(deepest) >= 1 && d[deepest] === 'withdrawn') return APPEAL_STATUS.withdrawn;
    if (deepest && levelOrdinal(deepest) >= 1 && d[deepest] === 'denied') return APPEAL_STATUS.denied;
    if (Object.values(d).includes('accepted')) return APPEAL_STATUS.value_accepted;
    if (deepest && levelOrdinal(deepest) >= 1) return APPEAL_STATUS.appeal_open;
    return APPEAL_STATUS.under_review;
  };

  // ═══ A CORRECTED NOTICE REPLACES THE ORIGINAL in the Current view (Philip 2026-10-23, docs/corrected-notice-replaces-plan.md) ═══
  // When the year has a Corrected notice with values (the latest, if several) and the original has NO REAL RESULT —
  // its final level is not an appeal level, or the outcome is not final, or nothing came off (withdrawn, denied, none)
  // — the year's row is the Corrected notice's. It is figured on a re-levelled copy of the year, the Miami 30-3129 /
  // 2023 layout: Proposed = the original's Proposed; Appeal One = the Corrected notice's Proposed, ticked Non-NTRG;
  // Appeal Two … = the Corrected notice's own appeals. So with the Non-NTRG switch off, Proposed / Final and savings
  // come from the Corrected notice and its own appeals only; switched on, the original → corrected drop counts, marked
  // Non-NTRG. A Corrected notice with no appeal and no decision is final (the county's corrected value stands, $0).
  // An original with a real result (an Accepted appeal that took value off) keeps its row and NTRG savings.
  // → pushRow's arguments for the stand-in row, or null.
  const correctedStandIn = (original, k, label, common) => {
    const nos = [...noticeLvls.keys()].filter((nk) => nk.startsWith(`${k}|`) && noticeNames.get(nk)?.key === 'corrected')
      .map((nk) => Number(nk.split('|')[2])).sort((a, b) => b - a);
    if (!nos.length) return null;
    const nk = `${k}|${nos[0]}`;
    const lv = noticeLvls.get(nk) || {};
    if (!(lv.proposed || []).length) return null;
    const of = parcelYearFigures({ ...common, lvlRows: original.lvlRows, decisions: original.dec, status: original.status, manual: null, label, billedTotal: null });
    if (of.isFinal && levelOrdinal(of.finalLevel) >= 1 && (toNumber(of.reduction) || 0) > 0) return null;   // a real result stands
    const dec = decByLvl.get(nk) || {};
    const ownAppeal = Object.keys(lv).some((l) => levelOrdinal(l) >= 1 && (lv[l] || []).length);
    const status = ownAppeal ? noticeStatus(lv, dec) : APPEAL_STATUS.value_accepted;
    const origProposed = original.lvlRows.proposed || [];
    // No original Proposed to measure from: the Corrected notice's own levels, as they are.
    if (!origProposed.length) {
      return { ...original, rowKey: k, lvlRows: lv, dec, status, ticked: false, noticeLabel: 'Current', noticeType: noticeNames.get(nk)?.name || 'Corrected' };
    }
    const up = (l) => levelKeyAt(levelOrdinal(l) + 1);
    const shifted = { proposed: origProposed };
    Object.entries(lv).forEach(([l, rows]) => { if (levelOrdinal(l) >= 0) shifted[up(l)] = rows.map((r) => ({ ...r, appeal_level: up(l) })); });
    const shiftedDec = Object.fromEntries(Object.entries(dec).map(([l, d]) => [up(l), d]));
    return {
      ...original, rowKey: k, lvlRows: shifted, dec: shiftedDec, status, ticked: true,
      showLvls: lv, unshift: (l) => (l && levelOrdinal(l) >= 1 ? levelKeyAt(levelOrdinal(l) - 1) : l),
      noticeLabel: 'Current', noticeType: noticeNames.get(nk)?.name || 'Corrected',
      jurs: new Set(Object.values(lv).flat().map((r) => r.jurisdiction).filter(Boolean)),
    };
  };

  const manualMap = new Map();
  manual.forEach((r) => {
    const k = key(r.parcel_id, String(r.tax_year));
    const m = manualMap.get(k) || { proposed: null, final: null };
    const p = toNumber(r.proposed_tax); const f = toNumber(r.final_tax);
    if (p != null) m.proposed = (m.proposed ?? 0) + p;
    if (f != null) m.final = (m.final ?? 0) + f;
    manualMap.set(k, m);
  });

  // Each parcel's bills by year, for parcelYearFigures' rate lookup (this year's bill, else the latest prior one).
  const billsByParcel = new Map();
  bills.forEach((b) => {
    const m = billsByParcel.get(b.parcel_id) || new Map();
    const y = String(b.tax_year);
    m.set(y, [...(m.get(y) || []), b]);
    billsByParcel.set(b.parcel_id, m);
  });
  const refsByParcel = new Map();
  references.forEach((r) => {
    const m = refsByParcel.get(r.parcel_id) || new Map();
    const y = String(r.tax_year);
    m.set(y, [...(m.get(y) || []), r]);
    refsByParcel.set(r.parcel_id, m);
  });

  // billedYearTotal's inputs: each parcel-year's active collectors, and each collector-year-kind's scheduled installments.
  const collectorsByKey = new Map();
  links.forEach((l) => {
    if (l.active === false || !l.collector_id) return;
    const k = key(l.parcel_id, String(l.tax_year));
    collectorsByKey.set(k, (collectorsByKey.get(k) || new Set()).add(l.collector_id));
  });
  const instsByKey = new Map();
  schedule.forEach((r) => {
    const k = `${r.collector_id}|${r.tax_year}|${r.kind}`;
    instsByKey.set(k, [...new Set([...(instsByKey.get(k) || []), Number(r.installment_number)])]);   // a duplicated schedule row counts once
  });
  // The card's rule (ParcelModal isREp): blank is Real Estate. Real-and-Personal has no single kind → no billed total.
  const kindOf = (parcel) => {
    const t = String(parcel.re_pp || 'Real Estate').trim().toUpperCase();
    return t === 'REAL ESTATE' ? 'RE' : t === 'PERSONAL PROPERTY' ? 'PP' : null;
  };

  const rows = [];
  wantYears.forEach((year) => {
    const live = allowed.get(year) || new Set();
    const base = baseline.get(year) || new Set();
    const resolvedAssessors = assessorByYear.get(year);
    // Every parcel-year that carries ANY data. A represented parcel with no appeal and no bill
    // still belongs on the report — its Taxes cell reads BLANK, which says "no data". A zero
    // would say "no tax due", and in front of a client that is a different statement.
    const keys = new Set();
    [statusMap, rowsByLvl, manualMap].forEach((m) => m.forEach((_v, k) => { if (k.endsWith(`|${year}`)) keys.add(k); }));
    bills.forEach((b) => { if (String(b.tax_year) === year) keys.add(key(b.parcel_id, year)); });

    keys.forEach((k) => {
      const pid = k.split('|')[0];
      // ⛔ A BASELINE YEAR IS ADMITTED AND MARKED, not dropped. It carries the client's figures from
      // before we were engaged, which is the whole point of loading it — excluding it made the data
      // write-only. Anything that is neither represented nor baseline is still dropped.
      const isBaseline = !live.has(pid) && base.has(pid);
      if (!live.has(pid) && !isBaseline) return;        // neither ours nor baseline that year
      const parcel = parcelById.get(pid);
      if (!parcel) return;
      const prop = propById.get(parcel.property_id) || {};
      const billedTotal = kindOf(parcel) ? billedYearTotal({
        bills: billsByParcel.get(pid)?.get(year), collectors: collectorsByKey.get(k),
        scheduleOf: (c) => instsByKey.get(`${c}|${year}|${kindOf(parcel)}`) || [],
      }) : null;
      const common = { savedRates: rateByJur.get(k), billsByYear: billsByParcel.get(pid), referencesByYear: refsByParcel.get(pid), year, state: parcel.state, basis };
      const shares = prorataShares(prorata, parcel, year);   // the client's pro-rata shares, or null (splitByProrata)

      // One row: the original notice (n = 0, exactly as before step 8) or an added notice (n ≥ 1, step 8).
      // showLvls / unshift: a Corrected notice standing in for the original (below) is figured on a re-levelled copy of
      // the year; the row's Level, Decision and per-level columns still name the Corrected notice's own levels.
      const pushRow = ({ rowKey, n, lvlRows, dec, status, ticked, manual, billed, jurs,
        showLvls = lvlRows, unshift = (l) => l, noticeLabel = null, noticeType = null }) => {
        const label = `${parcel.account_number || pid} / ${year}${n ? ` / notice ${n}` : ''}`;
        if (throughLevel) {
          const upTo = (l, lv = l) => levelOrdinal(lv) <= levelOrdinal(throughLevel);
          if (Object.entries(lvlRows).some(([l, rs]) => (rs || []).length && !upTo(l, unshift(l)))) {
            lvlRows = Object.fromEntries(Object.entries(lvlRows).filter(([l]) => upTo(l, unshift(l))));
            dec = Object.fromEntries(Object.entries(dec || {}).filter(([l]) => upTo(l, unshift(l))));
            showLvls = Object.fromEntries(Object.entries(showLvls).filter(([l]) => upTo(l)));
            status = APPEAL_STATUS.value_accepted;
          }
        }
        const f = parcelYearFigures({ ...common, lvlRows, decisions: dec, status, manual, label, nonNtrg: ticked && !includeNonNtrg, billedTotal: billed });
        // ⛔ PROJECTED, NOT DECIDED: with the option on, an undecided row shows what its levels so far would save — read
        // as if the deepest level with values were final. Off (the default), it reads Pending as before.
        const p = projectPending && !f.isFinal
          ? parcelYearFigures({ ...common, lvlRows, decisions: dec, status: APPEAL_STATUS.value_accepted, manual, label, nonNtrg: ticked && !includeNonNtrg, billedTotal: billed })
          : null;
        const fig = p || f;
        const { decision, level: decisionLevel } = rowDecision(dec, f.finalLevel);
        const row = {
          __key: rowKey,
          tax_year: year,
          // ⛔ THE GROUPING KEY. Its absence is what made Portfolio Tax Trend group every parcel in
          // the client under `undefined|2021` — one bucket, labelled with whichever property sorted
          // first, holding the whole client's figures. Any report that rolls these rows up groups on
          // THIS, never on the display name, which is not unique and not stable.
          property_id: parcel.property_id ?? null,
          property: prop.name || parcel.parcel_name || parcel.property_id,
          client_id: prop.client_id || null,
          account_number: parcel.account_number || '',
          // The parcel itself and its representation span — Portfolio Tax Trend names a year whose parcel set changed
          // (added / retired / reactivated) from these. Not columns on any report.
          parcel_id: pid,
          parcel_first_year: parcel.first_year ?? null,
          parcel_retired_year: parcel.retired_year ?? null,
          parcel_reactivated_year: parcel.reactivated_year ?? null,
          assessor: resolvedAssessors?.byParcelId?.get(pid)?.name || '',
          state: prop.state || parcel.state || '',
          address: parcel.address || prop.address || '',
          // For the reports that group by authority or by outcome. Joined when a parcel-year spans
          // several jurisdictions, because the values on this row are summed across all of them —
          // attributing the row to one of them would be a claim the data does not support.
          // With a marked baseline the figures are ONE base's, so the label names that base (e.g. "School").
          // With a base picked by its reduction (marked or largest), the figures are that base's, so the label names it.
          jurisdiction: f.head ? jurisdictionLabel(f.head.final) : f.shownBase ? jurisdictionLabel(f.shownBase)
            : [...new Set([...(jurs || [])].map(jurisdictionLabel))].sort().join(' + '),
          decision: decision || null,
          decision_level: unshift(decisionLevel),
          location_code: prop.location_code || '',
          appeal_level: appealLevelLabel(unshift(f.finalLevel)),
          appeal_level_key: unshift(f.finalLevel) || null,   // the raw ordinal, for the PDF's "Level - Status" pairing
          appeal_status: status ?? null,
          proposed_value: f.proposed_value,
          final_value: f.final_value,
          proposed_market: f.proposed_market,
          final_market: f.final_market,
          // ⛔ NO SAVINGS ARE CLAIMED AGAINST A BASELINE YEAR, AND THAT IS ENFORCED HERE RATHER THAN
          // LEFT TO THE FOOTNOTE. sumColumn adds whatever a row carries, so a baseline row with a
          // savings figure would land in the client-facing total no matter what the note said. NULL and
          // not 0 — a zero asserts "we saved nothing", which is a different claim from "we were not
          // engaged", and viewModel already distinguishes them (a year with no figure reads blank).
          // Same for reduction: a reduction implies an appeal we did not file.
          // PENDING, not blank and not 0: the outcome is not known yet (Appeal Open / Under Review / Value Pending).
          reduction: isBaseline ? null : fig.reduction,
          // NTRG's part of the reduction — the same, except after a Non-NTRG Appeal One (step 7). Appeal Results' realised.
          ntrg_reduction: isBaseline ? null : fig.ntrg_reduction,
          non_ntrg: !!ticked,
          // An added notice's tax is not the year's bill (that is the original's): blank rather than counted twice.
          taxes: n ? null : f.taxes,
          savings: isBaseline ? null : fig.savings,
          projected: !!p,
          // Row-level for the screen (pill + muted row) and a plain text field so a CSV or XLSX row is
          // marked too — the footnote must have something to point at in the file a client opens.
          baseline: isBaseline,
          // 'Non-NTRG': the year's Appeal One was someone else's appeal — its reduction shows, NTRG's savings start after it.
          engagement: isBaseline ? 'Baseline year' : ticked ? 'Non-NTRG' : '',
          // The raw percent sum (1.178467), gated on normaliseRate having ACCEPTED it: a rate
          // refused as implausible must not appear in a column someone would multiply by. Blank
          // instead. Reading sumRate rather than consolidated*100 avoids a needless float round-trip.
          // ⛔ The rate carries the RECOVERED mark but never the ESTIMATE one. A carried-forward rate
          // is the prior year's real printed rate — accurate as a rate, merely from another year, and
          // the taxes it produces are already asterisked. A recovered rate is this year's, but
          // derived rather than transcribed, and that belongs on the rate cell above all others.
          // ⛔ PUBLISHED AS A PERCENT, NOT AS THE RAW CELL SUM. This printed split.sumRate — fine while every
          // bill was Californian and quoted percent, and a 10x error the moment a millage bill's rate stopped
          // being refused: Σ 13.4686 would have gone out in a client report's EffectiveTaxRate column where
          // 1.346860 belongs. consolidated is the normalised DECIMAL, so ×100 is the percent the column means,
          // and it equals split.sumRate exactly on a percent bill.
          // ⛔ WHEN TIER 2 SUPPLIED THE TAX, THE CARRIED RATE MUST NOT BE PUBLISHED BESIDE IT. It would
          // contradict the figure: 435200IN would print 2.008000 next to a total derived from 1.3675% and
          // 0.6287%, and anyone multiplying would get a third answer. With two bases there IS no single
          // effective rate — the same reason computeTaxBill publishes none on its multi path and
          // JurisdictionBreakdown blanks its consolidated rate on mixed bases. One base gets its own entered
          // rate; more than one gets a blank.
          // Per-base savings use a different rate per base — there is no single effective rate to print beside them.
          rate: f.rate,
          // The Tax Rates report: the year's actual rate (no carried or estimated one), its source and its taxable base.
          actual_rate: f.actualRate,
          equalizer: f.equalizer,   // the year's State Equalizer (Cook), or null
          rate_source: f.rateSource,
          billable_taxable: f.billableTaxable,
          notice_no: n,
          notice: nameNotices ? (noticeLabel || (n ? noticeNames.get(rowKey)?.name || `Notice ${n}` : 'Original')) : null,
          notice_type: noticeType || (n ? noticeNames.get(rowKey)?.name || `Notice ${n}` : null),
        };
        // LEVELS SIDE BY SIDE (step 8): each level's value, tax (its taxable × the year's rate) and savings from Proposed,
        // read as if that level were final. Optional columns, hidden until ticked in Columns.
        LEVEL_KEYS.forEach((L) => {
          if (!(showLvls[L] || []).length) return;
          const upTo = Object.fromEntries(Object.entries(showLvls).filter(([l]) => levelOrdinal(l) >= 0 && levelOrdinal(l) <= levelOrdinal(L)));
          const fl = parcelYearFigures({ ...common, lvlRows: upTo, decisions: {}, status: APPEAL_STATUS.value_accepted, manual: null, label, billedTotal: null });
          // Each level shows the row's base when the single figures picked one (same name), read at the level fl
          // resolved as final — an open level with no values yet falls back as the primary does. Otherwise exactly
          // as before: that level's marked headline, else its primary column. (A picked base and a marked baseline
          // never meet: the row picks only when it has no headline, and markers give it one.)
          const col = sameJurisdictionRow(showLvls[L], f.shownBase) || primaryAppealRow(showLvls[L], `${label} ${L}`);
          const taxable = toNumber(col?.taxable_total);
          const ratePct = toNumber(f.rate);
          const baseCol = f.shownBase ? sameJurisdictionRow(upTo[fl.finalLevel || 'proposed'], f.shownBase) : null;
          const baseValue = baseCol ? (basis === 'market' ? toNumber(baseCol.market_total) ?? toNumber(baseCol.taxable_total)
            : toNumber(baseCol.taxable_total) ?? toNumber(baseCol.market_total)) : null;
          // ⛔ THE ENTERED APPEAL TAX $ (Philip 2026-10-23): when it set the row's savings, the row's final level shows
          // the same figures, unmarked — Final Tax $ as its taxes, Proposed − Final as its savings — not the rate estimate.
          const entered = f.card.fromEntered && L === unshift(f.finalLevel);
          row[`lvl_${L}_value`] = baseValue ?? (fl.head ? fl.final_value : fl.primaryFinalValue);
          row[`lvl_${L}_taxes`] = entered ? manual.final : taxable != null && ratePct != null ? taxable * (f.equalizer || 1) * ratePct / 100 : null;
          if (L !== 'proposed') row[`lvl_${L}_savings`] = isBaseline ? null : entered ? f.card.savings : fl.savings;
        });
        rows.push(...splitByProrata(row, shares, propById));
      };

      const original = { rowKey: k, n: 0, lvlRows: rowsByLvl.get(k) || {}, dec: decByLvl.get(k), status: statusMap.get(k), ticked: nonNtrg.has(k),
        manual: manualMap.get(k), billed: billedTotal, jurs: jurMap.get(k) };
      const replacement = wantCurrent ? correctedStandIn(original, k, `${parcel.account_number || pid} / ${year}`, common) : null;
      if (replacement) pushRow(replacement);
      // The original as entered: picked by name, or as the Current value when no Corrected notice replaces it.
      if (noticePicked('original') || (wantCurrent && !replacement)) {
        pushRow({ ...original, noticeLabel: noticePicked('original') ? 'Original' : 'Current' });
      }
      // Each added notice whose type is picked (or All), in notice order.
      [...noticeLvls.keys()].filter((nk) => nk.startsWith(`${k}|`)).map((nk) => Number(nk.split('|')[2])).sort((a, b) => a - b).forEach((n) => {
        const nk = `${k}|${n}`;
        if (!noticePicked(noticeNames.get(nk)?.key)) return;
        const lv = noticeLvls.get(nk);
        pushRow({ rowKey: nk, n, lvlRows: lv, dec: decByLvl.get(nk), status: noticeStatus(lv, decByLvl.get(nk)), ticked: nonNtrg.has(nk),
          manual: null, billed: null, jurs: new Set(Object.values(lv).flat().map((r) => r.jurisdiction).filter(Boolean)) });
      });
    });
  });

  // ⛔ SORTED HERE, not via defaultSort — state, then property, then year, matching how the
  // analyst's report reads. ReportView's sort takes a single key; a three-level order belongs to
  // the definition, and doing it here needs no change to the generic view.
  rows.sort((a, b) =>
    String(a.state).localeCompare(String(b.state))
    || String(a.property).localeCompare(String(b.property))
    || String(a.tax_year).localeCompare(String(b.tax_year)));
  return rows;
}
